import { component } from "picoapp";
import { findSelectedVariants, formatMsrpValue } from "@tablescape/utilities";
import { type Context } from "@tablescape/state";
import {
  STOREFRONT_CART_ATTRIBUTES,
  STOREFRONT_CART_GIFT_CARD_ATTRIBUTES,
} from "@tablescape/constants";
import { TablescapeContext, TablescapeEvent } from "@tablescape/context";
import { takeAddToCartAction } from "@tablescape/components/Buttons/actions/tbsp-button-pdp-add-to-cart";

const showButtons = (buttons: HTMLDivElement[]) => {
  buttons.forEach((button: HTMLDivElement) => {
    button.classList.remove("hidden");
    button.removeAttribute("aria-hidden");
  });
};

export const productFormButton = component<Context>((node) => {
  const isGiftCard = node.dataset.giftCard === "true";

  if (isGiftCard) {
    TablescapeContext.on(TablescapeEvent.ADD_GIFT_CARD_TO_CART, (data) => {
      if (!data || !data.giftCard || !data.selectedVariant) return;

      const variantId = data.selectedVariant.id;
      const variantMsrp =
        Array.from(
          document.querySelectorAll<HTMLOptionElement>(
            `form[id="product-form"] select[name="variant-options"] option`
          )
        ).find((option: HTMLOptionElement) => option.value === variantId)
          ?.dataset.productVariantMsrp ?? null;

      if (!variantMsrp) return;

      const lines = [
        {
          attributes: [
            {
              key: STOREFRONT_CART_ATTRIBUTES.MSRP,
              value: formatMsrpValue(variantMsrp),
            },
            {
              key: STOREFRONT_CART_GIFT_CARD_ATTRIBUTES.ID,
              value: String(data.giftCard.giftCardId),
            },
            {
              key: STOREFRONT_CART_GIFT_CARD_ATTRIBUTES.NAME,
              value: String(data.giftCard.name),
            },
            {
              key: STOREFRONT_CART_GIFT_CARD_ATTRIBUTES.MESSAGE,
              value: String(data.giftCard.message),
            },
            {
              key: STOREFRONT_CART_GIFT_CARD_ATTRIBUTES.EMAIL,
              value: String(data.giftCard.email),
            },
          ],
          id: variantId,
          quantity: 1,
        },
      ];
      takeAddToCartAction(lines);
    });
  }

  const isProductBundle = node.dataset.productBundle === "true";
  const isShopifyBundle = node.dataset.shopifyBundle === "true";

  const addToCart = node.querySelectorAll<HTMLDivElement>(
    "[data-product-form-button-atc]"
  );
  const waitlist = node.querySelectorAll<HTMLDivElement>(
    "[data-product-form-button-waitlist]"
  );
  const soldOut = node.querySelectorAll<HTMLDivElement>(
    "[data-product-form-button-soldout]"
  );

  TablescapeContext.on(TablescapeEvent.VARIANT_SELECTED, (data?) => {
    if (!data || !data.products || !data.selectedVariant) return;

    [
      ...Array.from(addToCart),
      ...Array.from(waitlist),
      ...Array.from(soldOut),
    ].forEach((button: HTMLDivElement) => {
      button.classList.add("hidden");
      button.setAttribute("aria-hidden", "true");
    });

    const selectedVariants = findSelectedVariants(data.products);

    const isLegacyVariantBundle = selectedVariants.every(
      (variant) => !!variant.variantBundleComponentImages?.length
    );

    if (isLegacyVariantBundle) {
      const isVariantBundleAvailable = selectedVariants.every((variant) =>
        variant.variantBundleKit
          ? variant.variantBundleKit?.availableForSale
          : variant.variantBundleAvailableForSale
      );
      showButtons(Array.from(isVariantBundleAvailable ? addToCart : soldOut));
      return;
    }

    const isAvailable = selectedVariants.every((variant) =>
      variant.variantBundleKit
        ? variant.variantBundleKit?.availableForSale
        : variant.availableForSale
    );
    const isWaitlisted =
      !selectedVariants.some((variant) => variant.disableWaitlist) &&
      !selectedVariants.some((variant) => !!variant.variantBundleKit) &&
      !isProductBundle &&
      !isShopifyBundle; // waitlisted if it's not a product bundle and not disabled by the variant metafield

    showButtons(
      Array.from(isAvailable ? addToCart : isWaitlisted ? waitlist : soldOut)
    );
  });
});
